import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Copy, ArrowRight, Clock, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { COURSE_LIST, CATEGORY_LABELS } from "@/data/courses";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";

const ORDER = ["einstieg", "erfahren", "fuehrung", "pflicht"];

export default function Kurse() {
  useEffect(() => {
    document.title = "Alle Kurse & Weiterbildungen · Dickies-Campus";
    window.scrollTo(0, 0);
  }, []);

  const copyLink = async (id, title) => {
    const url = `https://dickies-helden.de/kurse/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Link kopiert: ${title}`);
    } catch {
      window.prompt("Link zum Kopieren:", url);
    }
  };

  return (
    <div className="min-h-screen bg-cream-100">
      <Header />
      <main className="max-w-6xl mx-auto px-5 pt-28 pb-20">
        <p className="text-xs font-semibold uppercase tracking-widest text-terra">Kursübersicht</p>
        <h1 className="mt-2 font-heading font-bold text-4xl sm:text-5xl text-sand-800 tracking-tight">Alle Kurse auf einen Blick.</h1>
        <p className="mt-3 text-sand-600 max-w-2xl">
          100 % online · live unterrichtet · Start jeden Montag. Jeder Kurs hat seine eigene Seite –
          einfach anklicken oder den Link direkt kopieren.
        </p>

        {ORDER.map((cat) => {
          const list = COURSE_LIST.filter((c) => c.category === cat);
          if (!list.length) return null;
          return (
            <section key={cat} className="mt-12">
              <h2 className="font-heading font-bold text-lg text-sand-800 border-b border-cream-300 pb-2">
                {CATEGORY_LABELS[cat]} <span className="text-sand-600 font-normal text-sm">· {list.length} Kurse</span>
              </h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((c) => (
                  <div
                    key={c.id}
                    data-testid={`kurs-card-${c.id}`}
                    className="group rounded-2xl bg-white border border-cream-300 p-5 flex flex-col hover:shadow-hover hover:-translate-y-0.5 transition-all"
                  >
                    <Link to={`/kurse/${c.id}`} className="flex-1">
                      <h3 className="font-heading font-bold text-sand-800 leading-snug group-hover:text-terra transition-colors">{c.title}</h3>
                      <p className="mt-1 text-[13px] text-sand-500 leading-snug">{c.subtitle}</p>
                      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-sand-500">
                        <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{c.duration}</span>
                        {c.nextStart && <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{c.nextStart}</span>}
                      </div>
                      <p className="mt-2 text-sm font-semibold text-sand-800">{c.price}</p>
                    </Link>
                    <div className="mt-4 pt-3 border-t border-cream-200 flex items-center justify-between">
                      <Link
                        to={`/kurse/${c.id}`}
                        data-testid={`kurs-open-${c.id}`}
                        className="inline-flex items-center gap-1 text-[13px] font-semibold text-terra hover:underline"
                      >
                        Zur Kursseite <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                      <button
                        type="button"
                        data-testid={`kurs-copy-${c.id}`}
                        onClick={() => copyLink(c.id, c.title)}
                        title="Link kopieren"
                        className="inline-flex items-center gap-1 rounded-full border border-cream-300 px-3 py-1.5 text-[12px] text-sand-600 hover:bg-cream-100 transition-colors"
                      >
                        <Copy className="h-3.5 w-3.5" /> Link kopieren
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </main>
      <Footer />
    </div>
  );
}
